import { Component, OnInit } from '@angular/core';
import { IntegrationsettingsService } from './service/integrationsettings.service';
import { Integrationsetting } from '@src/app/models/integrationsetting';
import { SessionHelper } from '@src/app/helpers/sessionHelper';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-integrationsettings',
  templateUrl: './integrationsettings.component.html',
  styleUrls: ['./integrationsettings.component.css']
})
export class IntegrationsettingsComponent implements OnInit {
  integrationsetting:Integrationsetting;
  userEmail: string = '';
  testEmailPreview = {
    to: '',
    subject: '[WorkTrest] Email Delivery Test',
    bodyText: '',
    bodyHtml: ''
  };
  hasUnsavedChanges: boolean = false;
  isSaving: boolean = false;
  isSendingTestEmail: boolean = false;
  validationErrors: string[] = [];
  testEmailStatus: string = '';
  testEmailError: string = '';
  editstate: string;
  orgid: string;
  constructor(private issrv:IntegrationsettingsService) { }

  ngOnInit() {
    this.orgid = SessionHelper.getChoosenOrg();
    this.userEmail = String(SessionHelper.getUserEmail() || '').trim();
    this.integrationsetting = new Integrationsetting;
    this.issrv.getIntegrationsettings().pipe(untilDestroyed(this)).subscribe(is => {
      if(is != null){
        this.integrationsetting = is;
      }
      this.validationErrors = this.getValidationErrors();
      this.refreshTestEmailPreview();
      this.hasUnsavedChanges = false;
      this.editstate = '';
    });

  }
  saveIntegrationsettings(){
    this.hasUnsavedChanges = true;
    this.testEmailStatus = '';
    this.testEmailError = '';
    this.validationErrors = this.getValidationErrors();
    this.refreshTestEmailPreview();
    this.editstate = this.validationErrors.length > 0
      ? ' - Please fix validation errors before saving.'
      : ' - Unsaved changes.';
  }

  persistIntegrationsettings(){
    this.validationErrors = this.getValidationErrors();
    if (this.validationErrors.length > 0) {
      this.editstate = ' - Please fix validation errors before saving.';
      return;
    }

    this.isSaving = true;
    this.testEmailStatus = '';
    this.testEmailError = '';
    this.editstate = ' - Saving...';
    this.issrv.setIntegrationsettings(this.integrationsetting).pipe(untilDestroyed(this)).subscribe({
      next: () => {
        this.isSaving = false;
        this.hasUnsavedChanges = false;
        this.editstate = ' - Your changes are saved!';
      },
      error: () => {
        this.isSaving = false;
        this.editstate = ' - Failed to save changes. Please try again.';
      }
    });
  }

  canShowEmailTestSection(): boolean {
    return this.integrationsetting?.emailsetting === 'Standard(SMTP)'
      || this.integrationsetting?.emailsetting === 'SMTP2Go';
  }

  canSendTestEmail(): boolean {
    return this.canShowEmailTestSection()
      && !this.hasUnsavedChanges
      && !this.isSaving
      && !this.isSendingTestEmail
      && this.validationErrors.length === 0
      && this.testEmailPreview.to.length > 0;
  }

  sendTestEmail() {
    if (!this.canSendTestEmail()) {
      if (!this.userEmail) {
        this.testEmailError = 'Could not find your user email in session. Please sign in again.';
      } else if (this.hasUnsavedChanges) {
        this.testEmailError = 'Save settings before sending a test email.';
      }
      return;
    }

    this.refreshTestEmailPreview();
    const preview = this.testEmailPreview;
    this.isSendingTestEmail = true;
    this.testEmailStatus = 'Sending test email...';
    this.testEmailError = '';

    this.issrv.sendTestEmail({
      to: preview.to,
      subject: preview.subject,
      bodyText: preview.bodyText,
      bodyHtml: preview.bodyHtml,
      emailSetting: this.integrationsetting?.emailsetting,
      fromAddress: this.integrationsetting?.fromaddress,
      smtpServerUrl: this.integrationsetting?.smtpserverurl,
      smtpUsername: this.integrationsetting?.smtpusername,
      smtp2goApiKey: this.integrationsetting?.smtp2goapikey
    }).pipe(untilDestroyed(this)).subscribe({
      next: () => {
        this.isSendingTestEmail = false;
        this.testEmailStatus = 'Test email sent successfully.';
      },
      error: (error) => {
        this.isSendingTestEmail = false;
        this.testEmailStatus = '';
        const backendMessage = String(
          (error && error.error && (error.error.details || error.error.error))
          || (error && error.message)
          || ''
        ).trim();
        this.testEmailError = backendMessage
          ? 'Failed to send test email: ' + backendMessage
          : 'Failed to send test email. Verify settings and try again.';
      }
    });
  }

  private refreshTestEmailPreview() {
    const deliveryMethod = this.integrationsetting?.emailsetting || 'Unknown method';
    const to = this.userEmail;
    const subject = '[WorkTrest] Email Delivery Test';
    const fromAddress = String(this.integrationsetting?.fromaddress || 'Not set').trim() || 'Not set';
    const now = new Date();
    const bodyText = [
      'Hi,',
      '',
      'This is a test email from WorkTrest integration settings.',
      'Delivery method: ' + deliveryMethod,
      'From address: ' + fromAddress,
      'Sent at: ' + now.toISOString(),
      '',
      'If you received this, your email delivery setup is working.'
    ].join('\n');
    const bodyHtml = [
      '<h3>Email Delivery Test</h3>',
      '<p>This is a test email from WorkTrest integration settings.</p>',
      '<p><b>Delivery method:</b> ' + deliveryMethod + '</p>',
      '<p><b>From address:</b> ' + fromAddress + '</p>',
      '<p><b>Sent at:</b> ' + now.toISOString() + '</p>',
      '<p>If you received this, your email delivery setup is working.</p>'
    ].join('');

    this.testEmailPreview = { to, subject, bodyText, bodyHtml };
  }

  private getValidationErrors(): string[] {
    const errors: string[] = [];
    const emailSetting = String(this.integrationsetting?.emailsetting || '').trim();
    const fromAddress = String(this.integrationsetting?.fromaddress || '').trim();
    const smtpServerUrl = String(this.integrationsetting?.smtpserverurl || '').trim();
    const smtpUsername = String(this.integrationsetting?.smtpusername || '').trim();
    const smtpPassword = String(this.integrationsetting?.smtppassword || '').trim();
    const smtp2goApiKey = String(this.integrationsetting?.smtp2goapikey || '').trim();

    if (emailSetting === 'Standard(SMTP)') {
      if (!smtpServerUrl) {
        errors.push('SMTP Server URL is required for Standard(SMTP).');
      }
      if (smtpServerUrl && !/^smtps?:\/\//i.test(smtpServerUrl)) {
        errors.push('SMTP Server URL must start with smtp:// or smtps://.');
      }
      if (!smtpUsername) {
        errors.push('SMTP Username is required for Standard(SMTP).');
      }
      if (!smtpPassword) {
        errors.push('SMTP Password is required for Standard(SMTP).');
      }
      if (!fromAddress) {
        errors.push('From address is required for Standard(SMTP).');
      }
    }

    if (emailSetting === 'SMTP2Go') {
      if (!smtp2goApiKey) {
        errors.push('API Key is required for SMTP2Go.');
      }
      if (!fromAddress) {
        errors.push('From address is required for SMTP2Go.');
      }
    }

    const filterPairs = [
      { key: this.integrationsetting?.filterfield1, value: this.integrationsetting?.filtervalue1, name: 'Filter 1' },
      { key: this.integrationsetting?.filterfield2, value: this.integrationsetting?.filtervalue2, name: 'Filter 2' },
      { key: this.integrationsetting?.filterfield3, value: this.integrationsetting?.filtervalue3, name: 'Filter 3' },
    ];

    const propertyPairs = [
      { key: this.integrationsetting?.propertyfield1, value: this.integrationsetting?.propertyvalue1, name: 'Property 1' },
      { key: this.integrationsetting?.propertyfield2, value: this.integrationsetting?.propertyvalue2, name: 'Property 2' },
      { key: this.integrationsetting?.propertyfield3, value: this.integrationsetting?.propertyvalue3, name: 'Property 3' },
    ];

    for (const pair of [...filterPairs, ...propertyPairs]) {
      const hasKey = String(pair.key || '').trim().length > 0;
      const hasValue = String(pair.value || '').trim().length > 0;
      if (hasKey !== hasValue) {
        errors.push(`${pair.name} must include both key and value.`);
      }
    }

    return errors;
  }

}
